import * as RadixTabs from '@radix-ui/react-tabs'
import { cn } from '../../lib/cn.js'

export interface TabItem {
  value: string
  label: React.ReactNode
  /** A count beside the label — "Live 24". Drawn quieter than the label. */
  count?: number
  disabled?: boolean
}

export interface TabsProps {
  items: TabItem[]
  value: string
  onValueChange: (value: string) => void
  className?: string
  children?: React.ReactNode
}

/**
 * Filters across the top of a table, and the two tabs a record opens with.
 *
 * A **tab**, not a segmented control: these narrow a list rather than choose a
 * mode, and the board draws them as a row of words with the active one underlined.
 */
export function Tabs({ items, value, onValueChange, className, children }: TabsProps) {
  return (
    /**
     * `min-w-0`, and it is the whole reason the strip scrolls.
     *
     * A flex item's `min-width` is `auto`, which means *"never smaller than
     * your content"* — so a `Tabs` inside a flex column grew to fit all nine
     * of Settings' tabs and pushed the **page** 178px wider than the phone,
     * while `overflow-x-auto` on the strip below never engaged because there
     * was nothing to overflow. The scroller was written, tested and correct,
     * and could not fire.
     *
     * Measured rather than guessed: the frame reported +178px and the
     * offending elements were the tab buttons themselves, sitting at x=414
     * through x=807 in a 386px viewport.
     */
    <RadixTabs.Root
      value={value}
      onValueChange={onValueChange}
      className={cn('min-w-0', className)}
    >
      {/*
        Scrollable sideways, and the scrollbar hidden.

        Settings has nine tabs. In a 900px window — a split screen, or a
        smaller laptop — they came to 651px inside a 596px column with
        `overflow: visible`, so the last two were **clipped off the edge with
        no way to reach them**: Content and The platform were simply gone.
        Nothing said so, because a clipped tab looks like a tab that is not
        there.
      */}
      {/* `shrink-0`, so a filling `Tabs` gives its height to the panel and
          the strip itself stays exactly one row tall and stays put. */}
      <RadixTabs.List className="border-hairline flex shrink-0 items-center gap-1 overflow-x-auto border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((t) => (
          <RadixTabs.Trigger
            key={t.value}
            value={t.value}
            disabled={t.disabled}
            className={cn(
              /*
               * `h-10`, matching every control it sits beside — it was `py-2`,
               * which came out 38px against a 40px search box.
               *
               * And **one weight, always.** The selected tab used to become
               * `font-medium`, so its label grew a pixel or two and every tab
               * to the right of it moved: clicking along a row of filters made
               * the row twitch under the cursor. The coral underline and the
               * darker ink already say which one is chosen; the weight was
               * saying it a third time, at the cost of the row holding still.
               */
              /* `shrink-0`, or the row squeezes every label to fit rather
                 than scrolling — which is how "Wellness lists" becomes
                 "Wellnes…" on a narrow window. */
              'relative -mb-px flex h-10 shrink-0 items-center gap-2 border-b-2 border-transparent px-3',
              'text-second text-ink-2 font-medium transition-colors outline-none',
              'hover:text-ink focus-visible:ring-2 focus-visible:ring-coral-fill/30',
              'disabled:opacity-50 disabled:pointer-events-none',
              'data-[state=active]:border-coral-fill data-[state=active]:text-ink',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="text-caption text-ink-muted tabular-nums">{t.count}</span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  )
}

export const TabPanel = RadixTabs.Content
